import { FC } from 'react';
import {
  Pwerm2CalculationWaterfallCaseYearMultipleDto,
  Pwerm2CalculationWaterfallCaseYearMultipleIrrBreakdownData,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountBasicFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { Alignment } from '@app/shared/enums/alignment.enum';
import styles from '../sponsor-irr-and-hurdle.module.scss';
import classNames from 'classnames';
import { useLocale } from '@app/core/hooks/useLocale';
import { EventColumn } from './SponsorIRRTable';

interface MultipleCashFlowProps {
  multipleId: string;
  eventColumns: EventColumn[];
  multipleData: Pwerm2CalculationWaterfallCaseYearMultipleDto;
}

const MultipleCashFlow: FC<MultipleCashFlowProps> = ({
  multipleId,
  eventColumns,
  multipleData,
}) => {
  const { l } = useLocale();
  const metricTitles = [
    { field: 'redemptions', label: l('_InfusionNegativeOrRedemption') },
    { field: 'cashInterest', label: l('_CashInterest') },
    { field: 'dividends', label: l('_Dividends') },
    { field: 'exitProceeds', label: l('_ExitProceeds') },
  ];

  const breakdownByOrderIndex = new Map<
    number,
    Pwerm2CalculationWaterfallCaseYearMultipleIrrBreakdownData
  >();
  Object.values(multipleData.institutionalIrrBreakdownData || {}).forEach((breakdown) => {
    breakdownByOrderIndex.set(breakdown.orderIndex, breakdown);
  });

  const hasValueByMetric: Record<string, boolean> = {};
  for (const { field } of metricTitles) {
    hasValueByMetric[field] = eventColumns.some((col) => {
      const breakdown = breakdownByOrderIndex.get(col.orderIndex);
      const value = breakdown ? breakdown[field as keyof typeof breakdown] : null;
      return value !== null && value !== undefined && value !== 0;
    });
  }

  return (
    <>
      {metricTitles.map(({ field, label }) => {
        // Check if any value for this metric is not null or zero, which will be displayed
        const hasValue = hasValueByMetric[field];

        if (!hasValue) {
          return null;
        }

        return (
          <tr key={`${multipleId}-${field}`}>
            <td
              className={classNames(
                styles['sub-multiple-header'],
                'table-primary__cell--vertical-separator'
              )}>
              {label}
            </td>
            {eventColumns.map((col) => {
              const breakdown = breakdownByOrderIndex.get(col.orderIndex);
              let cellValue = breakdown ? breakdown[field as keyof typeof breakdown] : null;
              if (cellValue === 0) {
                cellValue = null;
              }
              // Only display if the value is not null, not zero and not undefined
              if (cellValue !== null && cellValue !== undefined) {
                return (
                  <td key={`${multipleId}-${field}-${col.orderIndex}`}>
                    <CellValue
                      className={classNames(styles['sub-multiple'])}
                      value={cellValue}
                      {...amountBasicFieldFormattingProps}
                      alignment={Alignment.Right}
                      italics
                    />
                  </td>
                );
              } else {
                return breakdown ? (
                  <td key={`${multipleId}-${field}-${col.orderIndex}`} />
                ) : (
                  <td
                    key={`${multipleId}-${field}-${col.orderIndex}`}
                    className={styles['empty-cell']}
                  />
                );
              }
            })}
            <td />
          </tr>
        );
      })}
    </>
  );
};

export default MultipleCashFlow;
